/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardContent,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

interface FilterControlsProps {
  minDate: string;
  maxDate: string;
  defaultViewLevel: 'all' | 'year' | 'week' | 'day';
  viewLevel: 'all' | 'year' | 'week' | 'day';
  onViewLevelChange: (level: 'all' | 'year' | 'week' | 'day') => void;
  dateRange: { start: string; end: string };
  onDateRangeChange: (range: { start: string; end: string }) => void;
  showAllOption?: boolean;
  showAvailableOptions?: ('all' | 'year' | 'week' | 'day')[];
}

export const FilterControls: React.FC<FilterControlsProps> = ({
  defaultViewLevel,
  minDate,
  maxDate,
  viewLevel,
  onViewLevelChange,
  dateRange,
  onDateRangeChange,
  showAllOption = true,
  showAvailableOptions = ['all', 'year', 'week', 'day'],
}) => {
  
  React.useEffect(() => {
    if (!showAllOption && viewLevel === 'all') {
      onViewLevelChange(defaultViewLevel);
    }
  }, [showAllOption, viewLevel, onViewLevelChange]);

  const parseDate = (dateStr: string): Date => {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  const { realMinDate, realMaxDate } = React.useMemo(() => {
    try {
      const startDate = parseDate(minDate);
      const endDate = parseDate(maxDate);
      
      return {
        realMinDate: startDate,
        realMaxDate: endDate
      };
    } catch (error) {
      console.error('Error parsing date limits:', error);
      const today = new Date();
      return {
        realMinDate: new Date(today.getFullYear(), today.getMonth(), 1),
        realMaxDate: new Date(today.getFullYear(), today.getMonth() + 1, 0)
      };
    }
  }, [minDate, maxDate]);

  const startDateValue = React.useMemo(() => {
    if (!dateRange.start) return realMinDate;
    
    try {
      if (dateRange.start.includes('/')) {
        return parseDate(dateRange.start);
      }
      return new Date(dateRange.start);
    } catch (error) {
      console.error('Error parsing start date:', dateRange.start, error);
      return realMinDate;
    }
  }, [dateRange.start, realMinDate]);

  const endDateValue = React.useMemo(() => {
    if (!dateRange.end) return realMaxDate;
    
    try {
      if (dateRange.end.includes('/')) {
        return parseDate(dateRange.end);
      }
      return new Date(dateRange.end);
    } catch (error) {
      console.error('Error parsing end date:', dateRange.end, error);
      return realMaxDate;
    }
  }, [dateRange.end, realMaxDate]);

  const handleStartDateChange = (date: Date | null) => {
    if (!date) return;
    
    let adjustedDate = date;
    if (date < realMinDate) {
      adjustedDate = new Date(realMinDate);
    } else if (date > realMaxDate) {
      adjustedDate = new Date(realMaxDate);
    }
    
    const formattedDate = formatDateForInput(adjustedDate);
    
    const currentEndDate = endDateValue;
    if (adjustedDate > currentEndDate) {
      onDateRangeChange({ 
        start: formattedDate, 
        end: formatDateForInput(adjustedDate) 
      });
    } else {
      onDateRangeChange({ 
        start: formattedDate, 
        end: dateRange.end 
      });
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!date) return;
    
    let adjustedDate = date;
    if (date < realMinDate) {
      adjustedDate = new Date(realMinDate);
    } else if (date > realMaxDate) {
      adjustedDate = new Date(realMaxDate);
    }
    
    const formattedDate = formatDateForInput(adjustedDate);
    
    const currentStartDate = startDateValue;
    if (adjustedDate < currentStartDate) {
      onDateRangeChange({ 
        start: formatDateForInput(adjustedDate), 
        end: formattedDate 
      });
    } else {
      onDateRangeChange({ 
        start: dateRange.start, 
        end: formattedDate 
      });
    }
  };

  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  React.useEffect(() => {
    const needsReset = !dateRange.start || !dateRange.end || 
                      startDateValue.toString() === 'Invalid Date' || 
                      endDateValue.toString() === 'Invalid Date';
    
    if (needsReset) {      
      onDateRangeChange({
        start: formatDateForInput(realMinDate),
        end: formatDateForInput(realMaxDate)
      });
    }
  }, [realMinDate, realMaxDate, onDateRangeChange]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {showAvailableOptions.length > 0 &&<FormControl size="small" sx={{ minWidth: 120 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                Ver por
              </Typography>
              <Select
                value={viewLevel}
                onChange={(e) => onViewLevelChange(e.target.value as 'all' | 'year' | 'week' | 'day')}
                style={{height: '30px'}}
              >
                {showAllOption && showAvailableOptions.includes('all') && <MenuItem value="all">Todo</MenuItem>}
                {showAvailableOptions.includes('year') && <MenuItem value="year">Años</MenuItem>}
                {showAvailableOptions.includes('week') && <MenuItem value="week">Semanas</MenuItem>}
                {showAvailableOptions.includes('day') && <MenuItem value="day">Días</MenuItem>}
              </Select>
            </FormControl>}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                Desde
              </Typography>
              <DatePicker
                value={startDateValue}
                onChange={handleStartDateChange}
                minDate={realMinDate}
                maxDate={endDateValue}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { 
                      width: '140px',
                      '& .MuiInputBase-root': {
                        height: '30px',
                        fontSize: '0.875rem'
                      }
                    }
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                Hasta
              </Typography>
              <DatePicker
                value={endDateValue}
                onChange={handleEndDateChange}
                minDate={startDateValue}
                maxDate={realMaxDate}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { 
                      width: '140px',
                      '& .MuiInputBase-root': {
                        height: '30px',
                        fontSize: '0.875rem'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
};